import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, ChevronRight, ChevronDown, Eye, MapPin, Calendar, Users } from 'lucide-react';
import apiService from '../../services/apiService';

const STATUS = {
  active:   { cls: 'bg-green-100 text-green-700 border-green-200',  label: 'Active' },
  pending:  { cls: 'bg-amber-100 text-amber-700 border-amber-200',  label: 'Pending' },
  inactive: { cls: 'bg-slate-100 text-slate-500 border-slate-200',  label: 'Inactive' },
  rejected: { cls: 'bg-red-100 text-red-600 border-red-200',        label: 'Rejected' },
};

const RELATION = { S: 'Son', D: 'Daughter', W: 'Wife', H: 'Husband', B: 'Brother', F: 'Father', M: 'Mother' };

function calcAge(dob) {
  if (!dob) return null;
  const today = new Date(), birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function FmContribution({ f }) {
  if (f.promoted_member_id && f.promoted_member_status === 'active') return null;
  const age = calcAge(f.date_of_birth);
  if (age === null) return <span className="text-xs text-amber-500 font-medium">No DOB</span>;
  return age >= 21
    ? <span className="text-xs font-semibold text-emerald-600">£5/month</span>
    : <span className="text-xs text-slate-400">£0/month</span>;
}

function statusBadge(status) {
  const s = STATUS[status] || STATUS.inactive;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function ExpandedRow({ member, colSpan }) {
  const [family, setFamily] = useState(null);

  React.useEffect(() => {
    let live = true;
    apiService.getFamilyMembers(member.id)
      .then(d => { if (live) setFamily(d || []); })
      .catch(() => { if (live) setFamily([]); });
    return () => { live = false; };
  }, [member.id]);

  const formatDob = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; }
  };

  return (
    <tr className="bg-slate-50">
      <td colSpan={colSpan} className="px-0 py-0">
        <div className="px-6 py-4 border-t border-slate-100">
          {/* Address + DOB */}
          <div className="flex flex-wrap gap-6 mb-4">
            {member.address && (
              <div className="flex items-start gap-2 min-w-0">
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-400 font-medium mb-0.5">Address</p>
                  <p className="text-sm text-slate-700">{member.address}{member.postcode ? `, ${member.postcode}` : ''}</p>
                </div>
              </div>
            )}
            {member.date_of_birth && (
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-400 font-medium mb-0.5">Date of Birth</p>
                  <p className="text-sm text-slate-700">{formatDob(member.date_of_birth)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Family members */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-slate-400" />
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Family Members</p>
            </div>
            {family === null ? (
              <div className="flex items-center gap-2 py-2">
                <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
                <span className="text-sm text-slate-400">Loading…</span>
              </div>
            ) : family.length === 0 ? (
              <p className="text-sm text-slate-400 italic">No family members recorded</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {family.map(f => (
                  <div key={f.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-lg px-3 py-2">
                    <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 text-xs font-bold flex-shrink-0">
                      {f.name?.split(' ').slice(0, 2).map(n => n[0]).join('')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{f.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="inline-block text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                          {RELATION[f.relation] || f.relation}
                        </span>
                        <FmContribution f={f} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function MemberTable({ members, filters, setFilters, statusMap, isLoading, onMemberClick, onAddMember }) {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedId, setExpandedId] = useState(null);

  const sorted = useMemo(() => {
    return [...members].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === 'contribution') { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; }
      else { va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [members, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const toggleExpand = useCallback((e, id) => {
    e.stopPropagation();
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  const COLS = 8;

  const SortTh = ({ k, children, className = '' }) => (
    <th onClick={() => toggleSort(k)}
      className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-800 select-none whitespace-nowrap ${className}`}>
      {children} {sortKey === k && <span className="text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

      {/* Toolbar */}
      <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, ref, phone, postcode or family member…"
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>
          {/* Status filter */}
          <select
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition">
            <option value="all">All Statuses</option>
            {Object.entries(statusMap).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm text-slate-400 hidden sm:block">{members.length} members</span>
{onAddMember && (
            <button onClick={onAddMember}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm">
              <UserPlus className="w-4 h-4" /> Add Member
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-400">Loading members…</p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <Users className="w-10 h-10 text-slate-200" />
            <p className="text-sm text-slate-400">No members found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="w-10 px-3 py-3" />
                <SortTh k="membershipRef">Ref</SortTh>
                <SortTh k="name">Name</SortTh>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Family</th>
                <SortTh k="contribution" className="hidden sm:table-cell">Monthly</SortTh>
                <SortTh k="status">Status</SortTh>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(member => {
                const isExpanded = expandedId === member.id;
                return (
                  <React.Fragment key={member.id}>
                    <tr
                      className={`border-b border-slate-100 transition-colors cursor-pointer
                        ${isExpanded ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
                      onClick={(e) => toggleExpand(e, member.id)}
                    >
                      {/* Chevron */}
                      <td className="px-3 py-4 w-10">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200
                          ${isExpanded ? 'bg-blue-100 text-blue-600' : 'text-slate-400 hover:bg-slate-100'}`}>
                          {isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5" />
                            : <ChevronRight className="w-3.5 h-3.5" />}
                        </div>
                      </td>

                      {/* Reference */}
                      <td className="px-4 py-4 text-slate-500 font-mono text-xs">
                        {member.membershipRef || '—'}
                      </td>

                      {/* Name + badge */}
                      <td className="px-4 py-4">
                        <p className="font-semibold text-slate-800">{member.name}</p>
                        <span className="inline-block mt-0.5 text-xs bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded">
                          Account Holder
                        </span>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-4 hidden md:table-cell">
                        {member.phone && (
                          <p className="text-slate-600 text-xs flex items-center gap-1">
                            <span className="text-slate-400">☎</span> {member.phone}
                          </p>
                        )}
                        {member.email && (
                          <p className="text-slate-400 text-xs flex items-center gap-1 mt-0.5">
                            <span>✉</span> {member.email}
                          </p>
                        )}
                        {!member.phone && !member.email && <span className="text-slate-300 text-xs">—</span>}
                      </td>

                      {/* Family size */}
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {(member.family?.length || 0) > 0
                          ? <span className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-600 border border-indigo-100 px-2 py-1 rounded-full font-medium">
                              <Users className="w-3 h-3" /> {member.family.length} member{member.family.length !== 1 ? 's' : ''}
                            </span>
                          : <span className="text-slate-300 text-xs">—</span>}
                      </td>

                      {/* Monthly contribution */}
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <span className="font-semibold text-green-600">
                          £{parseFloat(member.subscriptionAmount || member.contribution || 0).toFixed(0)}/month
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4">
                        {statusBadge(member.status)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4 text-right" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/members/${member.id}`)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-lg transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" /> View Profile
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && <ExpandedRow member={member} colSpan={COLS} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
